/**
 * Silensa WebMCP-tools
 *
 * Shopify registreert op elke storefrontpagina al 12 standaardtools via zijn eigen adapter
 * (search_catalog, browse_store, get_product, show_variant, get_cart, add_to_cart,
 * update_cart_lines, cancel_cart, proceed_to_checkout, manage_orders,
 * search_shop_policies_and_faqs, get_ucp_profile). Die dupliceren we NIET.
 *
 * Dit bestand voegt alleen toe wat Shopify niet weet: advies per ruimte, akoestische
 * productfeiten (metafields), een indicatieve hoeveelheidsberekening, de Stick-configurator,
 * een adviesaanvraag en een Human-in-the-Middle review voor de checkout.
 *
 * Laden in layout/theme.liquid, direct na standard-actions-override.js:
 *   <script src="{{ 'silensa-webmcp.js' | asset_url }}" defer="defer"></script>
 *
 * API-transitie: Chrome 150+ biedt document.modelContext; navigator.modelContext blijft
 * alleen als fallback voor oudere builds in de origin trial.
 */
(() => {
  const REGISTERED = Symbol.for('silensa.webmcp.registered');
  const DRAFT_KEY = 'silensa:advies-concept';

  const ROOMS = {
    woonkamer: 'akoestiek-woonkamer',
    eetkamer: 'akoestiek-eetkamer',
    werkkamer: 'akoestiek-werkkamer',
    slaapkamer: 'akoestiek-slaapkamer',
    keuken: 'akoestiek-keuken',
    hal: 'akoestiek-hal',
    kinderkamer: 'akoestiek-kinderkamer',
  };

  // Productlabels uit de Shopify-tags; vrijstaand = roomdividers.
  const PLACEMENT_TAGS = {
    wand: ['wand', 'muurstickers'],
    plafond: ['plafond', 'plafondeilanden'],
    bureau: ['bureau', 'bureauschermen'],
    vrijstaand: ['roomdividers & plantenbakken'],
  };

  // Vuistregel voor het absorberend oppervlak als aandeel van het vloeroppervlak.
  // LET OP: laten bevestigen door de akoestisch adviseur (EASY Noise Control) voordat dit live gaat.
  const ABSORBER_RATIO = { licht: 0.1, duidelijk: 0.15, sterk: 0.25 };
  // Pas op true zetten nadat de adviseur de vuistregel heeft bevestigd.
  const ENABLE_ESTIMATE = false;

  const getModelContext = () => {
    if ('modelContext' in document && document.modelContext) return document.modelContext;
    if ('modelContext' in navigator && navigator.modelContext) return navigator.modelContext;
    return null;
  };

  const root = () => window.Shopify?.routes?.root || '/';
  const url = (path) => new URL(root() + path.replace(/^\//, ''), location.origin).toString();
  const result = (data) => ({ content: [{ type: 'text', text: JSON.stringify(data) }] });
  const isHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,99}$/.test(value);

  const navigate = (target) => {
    const go = window.Shopify?.routes?.navigate;
    if (typeof go === 'function') return go(target);
    window.location.assign(target);
  };

  async function fetchJson(target, signal) {
    const response = await fetch(target, { headers: { Accept: 'application/json' }, credentials: 'same-origin', signal });
    if (!response.ok) throw new Error(`HTTP ${response.status} voor ${target}`);
    return response.json();
  }

  // Laat de gebruiker een beslissing nemen. requestUserInteraction (indien beschikbaar)
  // pauzeert de agent totdat de mens heeft gereageerd.
  function askHuman(client, fn) {
    if (typeof client?.requestUserInteraction === 'function') return client.requestUserInteraction(fn);
    return fn();
  }

  // Toegankelijk, native <dialog> in huisstijl. Alle tekst via textContent: agent-input
  // wordt nooit als HTML geïnterpreteerd.
  function confirmDialog({ title, lines, confirmLabel, cancelLabel }) {
    return new Promise((resolve) => {
      const dialog = document.createElement('dialog');
      dialog.setAttribute('aria-labelledby', 'silensa-agent-dialog-title');
      dialog.style.cssText = 'max-width:44rem;width:calc(100% - 3.2rem);border:0;border-radius:1.2rem;padding:2.8rem;background:#F7F5EF;color:#1A1D1A;font:inherit;';

      const heading = document.createElement('h2');
      heading.id = 'silensa-agent-dialog-title';
      heading.textContent = title;
      heading.style.cssText = 'margin:0 0 1.2rem;font-size:2.2rem;';

      const note = document.createElement('p');
      note.textContent = 'Een AI-assistent vraagt dit namens jou. Er gebeurt niets zonder jouw klik.';
      note.style.cssText = 'margin:0 0 1.6rem;font-size:1.4rem;opacity:.75;';

      const list = document.createElement('ul');
      list.style.cssText = 'margin:0 0 2.4rem;padding-left:2rem;line-height:1.6;';
      for (const line of lines) {
        const item = document.createElement('li');
        item.textContent = line;
        list.append(item);
      }

      const actions = document.createElement('div');
      actions.style.cssText = 'display:flex;gap:1.2rem;flex-wrap:wrap;';
      const confirm = document.createElement('button');
      confirm.type = 'button';
      confirm.className = 'button';
      confirm.textContent = confirmLabel;
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'button button--secondary';
      cancel.textContent = cancelLabel;
      actions.append(confirm, cancel);

      dialog.append(heading, note, list, actions);
      document.body.append(dialog);

      const close = (approved) => {
        dialog.close();
        dialog.remove();
        resolve(approved);
      };
      confirm.addEventListener('click', () => close(true), { once: true });
      cancel.addEventListener('click', () => close(false), { once: true });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close(false);
      });

      dialog.showModal();
      cancel.focus(); // veilige standaardfocus: niet per ongeluk bevestigen
    });
  }

  const money = (cents, currency) =>
    new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency }).format(cents / 100);

  const tools = [
    {
      name: 'silensa_find_by_room',
      description:
        'Geeft akoestische producten die passen bij een kamer in huis en een montageplek (wand, plafond, bureau of vrijstaand), optioneel binnen een budget. Gebruik dit wanneer de gebruiker een galmprobleem in een specifieke ruimte beschrijft. Voegt niets toe aan de winkelwagen; gebruik daarvoor add_to_cart.',
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      inputSchema: {
        type: 'object',
        required: ['room'],
        properties: {
          room: { type: 'string', enum: Object.keys(ROOMS), description: 'De ruimte waar de galm hoorbaar is.' },
          placement: { type: 'string', enum: ['wand', 'plafond', 'bureau', 'vrijstaand', 'maakt_niet_uit'], default: 'maakt_niet_uit' },
          max_price_eur: { type: 'number', minimum: 1, description: 'Maximale vanaf-prijs per product in euro.' },
          limit: { type: 'integer', minimum: 1, maximum: 12, default: 6 },
        },
      },
      async execute({ room, placement = 'maakt_niet_uit', max_price_eur, limit = 6 }, client, options) {
        const handle = ROOMS[room];
        if (!handle) return result({ error: `Onbekende ruimte "${room}".`, rooms: Object.keys(ROOMS) });
        const { products } = await fetchJson(url(`collections/${handle}/products.json?limit=50`), options?.signal);
        const wanted = PLACEMENT_TAGS[placement];
        const matches = products
          .filter((p) => !wanted || p.tags.some((tag) => wanted.includes(tag.toLowerCase())))
          .map((p) => ({
            handle: p.handle,
            title: p.title,
            brand: p.vendor,
            type: p.product_type,
            price_from_eur: Math.min(...p.variants.map((v) => Number(v.price))),
            options: p.options.map((o) => ({ name: o.name, values: o.values })),
            url: url(`products/${p.handle}`),
          }))
          .filter((p) => !max_price_eur || p.price_from_eur <= max_price_eur)
          .slice(0, Math.min(limit, 12));
        return result({
          room,
          placement,
          collection_url: url(`collections/${handle}`),
          products: matches,
          tip: 'Twijfelt de gebruiker over kleur? Bied de Samplebox PET-vilt aan (handle samplebox-pet-vilt).',
        });
      },
    },
    {
      name: 'silensa_get_product_facts',
      description:
        'Geeft de akoestische en technische feiten van een Silensa-product: materiaal, dikte, absorptiewaarde (alpha w, NRC), brandklasse, montage, levertijd en retourbeleid. Gebruik dit voor productvergelijkingen; get_product levert deze velden niet. Velden zijn null als ze niet zijn vastgelegd: verzin dan geen waarde.',
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      inputSchema: {
        type: 'object',
        properties: {
          handle: { type: 'string', description: 'Product handle, bv. "panel-9-mm". Weglaten op een productpagina = huidig product.' },
        },
      },
      async execute({ handle } = {}, client, options) {
        const current = window.ShopifyAnalytics?.meta?.product ? location.pathname.split('/products/')[1]?.split(/[/?#]/)[0] : null;
        const target = handle || current;
        if (!isHandle(target)) return result({ error: 'Geef een geldige product handle mee.' });
        // Geen Accept: application/json hier: dan geeft Shopify zijn eigen product-JSON terug
        // in plaats van de alternatieve weergave ?view=agent-facts.
        const response = await fetch(url(`products/${target}?view=agent-facts`), { credentials: 'same-origin', signal: options?.signal });
        if (!response.ok) return result({ error: `Product "${target}" niet gevonden.` });
        return result(JSON.parse(await response.text()));
      },
    },
    {
      name: 'silensa_estimate_quantity',
      description:
        'Geeft een INDICATIE van hoeveel absorberend oppervlak (m2) een woonruimte nodig heeft en, als een product is opgegeven, hoeveel stuks dat ongeveer zijn. Dit is een vuistregel, geen akoestische berekening. Meld dat altijd aan de gebruiker en verwijs voor zekerheid naar silensa_request_advice.',
      annotations: { readOnlyHint: true },
      inputSchema: {
        type: 'object',
        required: ['length_m', 'width_m', 'galm'],
        properties: {
          length_m: { type: 'number', minimum: 1, maximum: 30 },
          width_m: { type: 'number', minimum: 1, maximum: 30 },
          galm: { type: 'string', enum: ['licht', 'duidelijk', 'sterk'], description: 'Hoe sterk de gebruiker de galm ervaart.' },
          product_handle: { type: 'string', description: 'Optioneel: product om stuks voor te berekenen.' },
        },
      },
      async execute({ length_m, width_m, galm, product_handle }, client, options) {
        const floor = length_m * width_m;
        const ratio = ABSORBER_RATIO[galm];
        if (!ratio) return result({ error: 'galm moet licht, duidelijk of sterk zijn.' });
        const target_m2 = Math.round(floor * ratio * 10) / 10;
        const output = { floor_area_m2: Math.round(floor * 10) / 10, indicative_absorber_m2: target_m2, disclaimer: 'Indicatie op basis van vloeroppervlak. Materiaal, dikte, plaatsing en inrichting bepalen het echte effect.' };

        if (isHandle(product_handle)) {
          const { product } = await fetchJson(url(`products/${product_handle}.json`), options?.signal);
          const sizes = [];
          for (const variant of product.variants) {
            const match = variant.title.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm/i);
            if (!match) continue;
            const piece = (parseFloat(match[1].replace(',', '.')) * parseFloat(match[2].replace(',', '.'))) / 10000;
            sizes.push({ variant: variant.title, piece_m2_bounding_box: Math.round(piece * 1000) / 1000, pieces: Math.ceil(target_m2 / piece), unit_price_eur: Number(variant.price) });
          }
          output.product = product_handle;
          output.per_size = sizes.filter((s, i, all) => all.findIndex((x) => x.piece_m2_bounding_box === s.piece_m2_bounding_box) === i);
          if (!output.per_size.length) output.note = 'Geen maten in cm gevonden voor dit product; vraag advies.';
        }
        return result(output);
      },
    },
    {
      name: 'silensa_open_stick_configurator',
      description: 'Opent de Silensa wandstudio (Stick-configurator) in de browser, waar de gebruiker zelf een patroon van zelfklevende vilttegels (Stick Hex, Kite, Lohex) ontwerpt. Gebruik bij vragen als "hoe ziet dit eruit op mijn muur".',
      annotations: { readOnlyHint: false },
      inputSchema: { type: 'object', properties: {} },
      async execute() {
        const target = url('pages/stick-configurator');
        navigate(target);
        return result({ navigating_to: target, instruction: 'De browser gaat naar de configurator. Navigeer niet opnieuw; laat de gebruiker ontwerpen.' });
      },
    },
    {
      name: 'silensa_request_advice',
      description:
        'Bereidt een adviesvraag voor aan de Silensa-specialisten (sinds 2011 actief in ruimteakoestiek). Vult het contactformulier vooraf in; de gebruiker controleert en verstuurt het ZELF. Deze tool verstuurt nooit iets.',
      annotations: { readOnlyHint: false },
      inputSchema: {
        type: 'object',
        required: ['message'],
        properties: {
          message: { type: 'string', maxLength: 2000, description: 'De vraag, met ruimte, afmetingen en waar de galm het meest hoorbaar is.' },
          name: { type: 'string', maxLength: 100 },
          email: { type: 'string', maxLength: 200, description: 'Alleen meegeven als de gebruiker het zelf in dit gesprek heeft opgegeven.' },
        },
      },
      async execute({ message, name, email }) {
        const draft = { message: String(message).slice(0, 2000), name: name ? String(name).slice(0, 100) : '', email: email ? String(email).slice(0, 200) : '' };
        // Concept via sessionStorage, nooit via de URL: persoonsgegevens horen niet in querystrings.
        try {
          sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        } catch {}
        if (document.getElementById('ContactForm')) {
          prefillContactForm();
          return result({ status: 'prefilled', instruction: 'Het formulier staat klaar. Vraag de gebruiker de gegevens te controleren en zelf op Versturen te klikken.' });
        }
        const target = url('pages/contact#ContactForm');
        navigate(target);
        return result({ status: 'navigating', navigating_to: target, instruction: 'Het formulier wordt op de contactpagina vooraf ingevuld. Vraag de gebruiker te controleren en zelf te versturen.' });
      },
    },
    {
      name: 'silensa_review_and_checkout',
      description:
        'Human-in-the-Middle stap voor afrekenen: toont de gebruiker een overzicht van de winkelwagen met levertijd en retourvoorwaarden en gaat pas naar de checkout als de gebruiker op "Naar afrekenen" klikt. Gebruik dit in plaats van proceed_to_checkout. De betaling zelf bevestigt de gebruiker altijd op de Shopify-checkout; plaats nooit zelf een bestelling.',
      annotations: { readOnlyHint: false },
      inputSchema: { type: 'object', properties: {} },
      async execute(input, client, options) {
        const cart = await fetchJson(url('cart.js'), options?.signal);
        if (!cart.item_count) return result({ status: 'empty_cart' });

        const lines = cart.items.map((item) => `${item.quantity} x ${item.product_title}${item.variant_title ? ` (${item.variant_title})` : ''}: ${money(item.final_line_price, cart.currency)}`);
        lines.push(`Subtotaal: ${money(cart.total_price, cart.currency)}, verzendkosten volgen bij het afrekenen`);
        lines.push('Levertijd: 4 tot 6 werkdagen (NL, BE, DE, LU)');
        lines.push('14 dagen bedenktijd voor standaardproducten; maatwerk is uitgesloten');

        const approved = await askHuman(client, () =>
          confirmDialog({ title: 'Klopt je bestelling?', lines, confirmLabel: 'Naar afrekenen', cancelLabel: 'Nog niet' })
        );
        if (!approved) return result({ status: 'declined_by_user', instruction: 'De gebruiker wil nog niet afrekenen. Vraag wat er moet veranderen.' });

        const target = url('checkout');
        navigate(target);
        return result({
          status: 'approved_by_user',
          navigating_to: target,
          instruction: 'Op de checkout bevestigt de gebruiker zelf adres en betaling. Gebruik daar alleen de checkout-tools van Shopify en dien de bestelling nooit in zonder uitdrukkelijke toestemming.',
        });
      },
    },
  ];

  // Contactpagina: zet een concept van silensa_request_advice in het formulier.
  function prefillContactForm() {
    const form = document.getElementById('ContactForm');
    if (!form) return;
    let draft;
    try {
      draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
      sessionStorage.removeItem(DRAFT_KEY);
    } catch {}
    if (!draft) return;
    const set = (selector, value) => {
      const field = form.querySelector(selector);
      if (field && value) field.value = value;
    };
    set('[name="contact[name]"]', draft.name);
    set('[name="contact[email]"]', draft.email);
    set('[name="contact[Onderwerp]"]', 'advice');
    set('[name="contact[body]"]', draft.message);
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    form.querySelector('[name="contact[body]"]')?.focus();
  }

  async function register() {
    const modelContext = getModelContext();
    if (!modelContext || typeof modelContext.registerTool !== 'function' || window[REGISTERED]) return;
    window[REGISTERED] = true;
    await Promise.allSettled(
      tools.filter((tool) => ENABLE_ESTIMATE || tool.name !== 'silensa_estimate_quantity').map(async (tool) => {
        try {
          await modelContext.registerTool({ ...tool, execute: (input, client, options) => tool.execute(input ?? {}, client, options) });
        } catch (error) {
          console.warn('[Silensa WebMCP] registratie mislukt:', tool.name, error);
        }
      })
    );
  }

  const start = () => {
    prefillContactForm();
    register();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
